import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { CenterPivot, Group, ResetPivot, Ungroup } from "slotworks/graphics/pixi/group";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

type PIXIElement = PIXI.Sprite | PIXI.Container | PIXI.Text;
type Ease = (k: number) => number;
const quad = TweenJS.Easing.Quadratic;

export function ScaleInFromCentre(object: PIXI.Container) {
    object.scale.set(0, 0);

    const size = new Point(object.width, object.height);
    const offset = new Point(object.x, object.y);

    new TweenJS.Tween(object.scale)
        .to({ x: 1, y: 1 }, 800)
        .onUpdate(() => {
            object.x = (size.x - object.width) * 0.5 + offset.x;
            object.y = (size.y - object.height) * 0.5 + offset.y;
        })
        .start();
}

export function Rotate(object: PIXI.Container, duration: number, radians: number, delay: number = 0) {
    const tween = new TweenJS.Tween(object)
        .delay(delay)
        .to({ rotation: radians }, duration);

    return new Contract<void>((resolve) => {
        tween.onComplete(() => resolve(null));
        tween.start();
    });
}

export function ZoomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None, spins: number = 0) {
    return Zoom(target, 0, 1, duration, delay, easing, spins);
}

export function ZoomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None, spins: number = 0) {
    return Zoom(target, 1, 0, duration, delay, easing, spins);
}

export function BounceZoomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, startSize: number = 0) {
    return Zoom(target, startSize, 1, duration, delay, TweenJS.Easing.Back.Out);
}

export function BounceZoomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0) {
    return Zoom(target, 1, 0, duration, delay, TweenJS.Easing.Back.In);
}

export function PivotBounceZoomIn(target: PIXIElement | PIXIElement[], duration: number, pivot: Point, delay: number = 0, startSize: number = 0) {
    return Zoom(target, startSize, 1, duration, delay, TweenJS.Easing.Back.Out, 0, pivot);
}

export function PivotBounceZoomOut(target: PIXIElement | PIXIElement[], duration: number, pivot: Point, delay: number = 0) {
    return Zoom(target, 1, 0, duration, delay, TweenJS.Easing.Back.In, 0, pivot);
}

export function SwipeFromTopIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, false, duration, delay, easing, 0);
}

export function SwipeFromTopOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, true, duration, delay, easing, 0);
}

export function SwipeFromLeftIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, false, duration, delay, easing, 3);
}

export function SwipeFromLeftOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, true, duration, delay, easing, 3);
}

export function SwipeFromBottomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, false, duration, delay, easing, 2);
}

export function SwipeFromBottomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, true, duration, delay, easing, 2);
}

export function SwipeFromRightIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, false, duration, delay, easing, 1);
}

export function SwipeFromRightOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: Ease = TweenJS.Easing.Linear.None) {
    return SwipeFromDirection(target, true, duration, delay, easing, 1);
}

export function FadeIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0) {
    return Fade(target, false, duration, delay);
}

export function FadeOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0) {
    return Fade(target, true, duration, delay);
}

export function EndlessHover(targets: PIXIElement | PIXIElement[], scale: number, duration: number, delay: number = 0, easing: Ease = quad.InOut): TweenJS.Tween {
    removeAllScaleTweens(targets);

    const target = Group(targets);

    if ((target as any).anchor) {
        const anchorable = target as any;
        if (anchorable.anchor.x !== 0.5) {
            anchorable.anchor.set(0.5);
            target.x += target.width * 0.5;
            target.y += target.height * 0.5;
        }
    } else {
        if (target.pivot.x === 0) {
            target.pivot.set(target.width * 0.5, target.height * 0.5);
            target.x += target.width * 0.5;
            target.y += target.height * 0.5;
        }
    }

    const targetInitialScale = new Point(target.scale.x, target.scale.y);
    const tweenScale = new Point(1, 1);

    const update = () => {
        if (!target) {
            TweenJS.remove(tween);
            return;
        } else {
            target.scale.set(targetInitialScale.x * tweenScale.x, targetInitialScale.y * tweenScale.y);
        }
    };

    const tween = new TweenJS.Tween(tweenScale)
        .to({ x: scale, y: scale }, duration)
        .easing(easing)
        .yoyo(true)
        .repeat(Infinity)
        .onUpdate(update);

    Timer.setTimeout(() => {
        tween.start();
    }, delay);

    return tween;
}

function Fade(targets: PIXIElement | PIXIElement[], out: boolean, duration: number, delay: number): Contract<void> {
    const target = Group(targets);
    target.alpha = out ? 1 : 0;

    const tween = new TweenJS.Tween(target)
        .delay(delay)
        .to({ alpha: out ? 0 : 1 }, duration);

    return new Contract<void>((resolve) => {
        tween.onComplete(() => {
            tween.onComplete(null);
            Ungroup(target);
            resolve(null);
        });
        tween.start();
    });
}

export function Blink(targets: PIXIElement | PIXIElement[], flickerCount: number, flickerDuration: number, delay: number): Contract<void> {
    const target = Group(targets);
    const opposite = target.alpha > 0 ? 0 : 1;

    const tween = new TweenJS.Tween(target)
        .to({ alpha: opposite }, flickerDuration)
        .repeat(flickerCount)
        .yoyo(true);

    return new Contract<void>((resolve) => {
        tween.onComplete(() => {
            tween.onComplete(null);
            resolve(null);
            Ungroup(target);
        });
        Timer.setTimeout(() => tween.start(), delay);
    });
}

function SwipeFromDirection(targets: PIXIElement | PIXIElement[], out: boolean, duration: number, delay: number, easing: Ease, direction: number = 0 | 2): Contract<void> {
    const target = Group(targets);

    const offScreenPosition: Point = new Point(target.x, target.y);
    const destination: Point = new Point();

    if (direction === 0) {
        // from top
        offScreenPosition.y = -target.height - target.parent.y;
    } else if (direction === 1) {
        // from left
        offScreenPosition.x = CanvasManager.stageWidth + target.parent.x;
    } else if (direction === 2) {
        // from bottom
        offScreenPosition.y = CanvasManager.stageHeight - target.parent.y;
    } else if (direction === 3) {
        // from right
        offScreenPosition.x = -target.width - target.parent.x;
    }
    destination.y = out ? offScreenPosition.y : target.y;
    destination.x = out ? offScreenPosition.x : target.x;

    if (!out) {
        target.y = offScreenPosition.y;
        target.x = offScreenPosition.x;
    }

    const tween = new TweenJS.Tween(target)
        .delay(delay)
        .to({ y: destination.y, x: destination.x }, duration)
        .easing(easing);

    return new Contract<void>((resolve) => {
        tween.onComplete(() => {
            tween.onComplete(null);
            Ungroup(target);
            resolve(null);
        });
        tween.start();
    });
}

export function Zoom(targets: PIXIElement | PIXIElement[],
                     scaleFrom: number, scaleTo: number, duration: number, delay: number, easing: Ease,
                     spins: number = 0, pivot: Point = null): Contract<void> {
    let target = CenterPivot(targets, null, pivot);

    const fromScale = new Point(scaleFrom * target.scale.x, scaleFrom * target.scale.y);
    const endScale = new Point(scaleTo * target.scale.x, scaleTo * target.scale.y);

    target.scale.set(fromScale.x, fromScale.y);

    const update = () => {
        if (target && target.scale) {
            if (spins > 0) {
                target.rotation = Math.PI * 2 * spins * (target.scale.x - fromScale.x) / (endScale.x - fromScale.x);
            }
        }
    };

    const tween = new TweenJS.Tween(target.scale)
        .delay(delay)
        .to(endScale, duration)
        .easing(easing)
        .onUpdate(update);

    return new Contract<void>((resolve) => {
        tween.onComplete(() => {
            tween.onComplete(null);
            ResetPivot(target);
            Ungroup(target);
            if (scaleTo === 0) {
                if (targets && (targets as any).length) {
                    for (const groupedTarget of targets as PIXIElement[]) {
                        groupedTarget.scale.x = groupedTarget.scale.y = scaleTo;
                    }
                }
            }
            if (target.name === "tempContainer") {
                target = null;
            }
            resolve(null);
        });
        tween.start();
    });
}

function removeAllScaleTweens(targets: PIXIElement | PIXIElement[]) {
    if ((targets as any).length) {
        for (const target of targets as PIXIElement[]) {
            TweenJS.removeAll(target.scale);
        }
    } else {
        TweenJS.removeAll((targets as PIXIElement).scale);
    }
}
